// src/store/notificationStore.ts
import { create } from 'zustand';
import { Notification } from '../types';
import { notificationService } from '../services/notification';

const DUPLICATE_NOTIFICATION_WINDOW_MS = 6 * 60 * 60 * 1000;

const getNotificationFamily = (jenis?: string) => {
  if (['laporan_diajukan', 'laporan_menunggu_validasi_l2'].includes(jenis ?? '')) {
    return 'approval_request';
  }

  if (jenis === 'laporan_menunggu_rapim') {
    return 'rapim_queue';
  }

  return jenis ?? '';
};

const dedupeNotifications = (notifications: Notification[]): Notification[] => {
  return notifications.filter((notification, index, allNotifications) => {
    const family = getNotificationFamily(notification.data.jenis);

    if (!['approval_request', 'rapim_queue'].includes(family)) {
      return true;
    }

    const currentTime = new Date(notification.created_at).getTime();

    return index === allNotifications.findIndex((candidate) => {
      const candidateFamily = getNotificationFamily(candidate.data.jenis);
      const candidateTime = new Date(candidate.created_at).getTime();

      return (
        candidateFamily === family &&
        candidate.data.url === notification.data.url &&
        candidate.data.title === notification.data.title &&
        candidate.data.message === notification.data.message &&
        Math.abs(candidateTime - currentTime) <= DUPLICATE_NOTIFICATION_WINDOW_MS
      );
    });
  });
};

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isDropdownOpen: boolean;
  isLoading: boolean;
  setNotifications: (notifications: Notification[]) => void;
  setUnreadCount: (count: number) => void;
  toggleDropdown: () => void;
  closeDropdown: () => void;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isDropdownOpen: false,
  isLoading: false,
  setNotifications: (notifications) => set({ notifications }),
  setUnreadCount: (count) => set({ unreadCount: count }),
  toggleDropdown: () => set((state) => ({ isDropdownOpen: !state.isDropdownOpen })),
  closeDropdown: () => set({ isDropdownOpen: false }),

  fetchNotifications: async () => {
    set({ isLoading: true });
    try {
      const notifications = await notificationService.getAll();
      const rawData = Array.isArray(notifications)
        ? notifications
        : (Array.isArray(notifications?.data?.data)
          ? notifications.data.data
          : (Array.isArray(notifications?.data) ? notifications.data : []));
      const data = dedupeNotifications(rawData);
      const unread = data.filter((n: Notification) => !n.read_at).length;
      set({ notifications: data, unreadCount: unread, isLoading: false });
    } catch (error) {
      console.error('Failed to fetch notifications', error);
      set({ isLoading: false });
    }
  },

  markAsRead: async (id) => {
    // Optimistic update
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read_at: new Date().toISOString() } : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));
    try {
      await notificationService.markAsRead(id);
    } catch (error) {
      // Revert if failed? For now, just log.
      console.error('Failed to mark notification as read', error);
    }
  },

  markAllAsRead: async () => {
    // Optimistic update
    set((state) => ({
      notifications: state.notifications.map((n) => ({
        ...n,
        read_at: n.read_at || new Date().toISOString(),
      })),
      unreadCount: 0,
    }));
    try {
      await notificationService.markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read', error);
    }
  },

  deleteNotification: async (id) => {
    set((state) => {
      const deletedNotification = state.notifications.find((n) => n.id === id);

      return {
        notifications: state.notifications.filter((n) => n.id !== id),
        unreadCount: deletedNotification && !deletedNotification.read_at
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount,
      };
    });
    try {
      await notificationService.delete(id);
    } catch (error) {
      console.error('Failed to delete notification', error);
    }
  }
}));
